/**
 * Letter of Recommendation Components
 * Manages letter of recommendation requests from students and handling by teachers
 */

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

/**
 * Custom hook for managing Letter of Recommendation state and handlers
 */
export const useLorManagement = ({ currentUser, userProfile }) => {
  const [lorRequests, setLorRequests] = useState([]);
  const [newLorRequest, setNewLorRequest] = useState({
    programName: '',
    deadline: '',
    purpose: '',
    emphasisPoints: '',
    additionalInfo: ''
  });

  // Listen for LoR requests
  useEffect(() => {
    if (!currentUser) return;

    const qLorRequests = query(collection(db, 'lorRequests'), orderBy('timestamp', 'desc'));
    const unsubscribeLorRequests = onSnapshot(qLorRequests, (snapshot) => {
      const lorRequestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLorRequests(lorRequestsData);
    }, (error) => console.error("Error fetching LoR requests:", error));

    return () => unsubscribeLorRequests();
  }, [currentUser]);

  // Submit a new LoR request (student)
  const handleSubmitLorRequest = async () => {
    if (!newLorRequest.programName.trim() || !newLorRequest.deadline || !newLorRequest.purpose.trim()) {
      alert('Please fill in all required fields (Program/Position, Deadline, and Purpose).');
      return;
    }

    try {
      await addDoc(collection(db, 'lorRequests'), {
        studentId: currentUser.uid,
        studentName: userProfile?.name || 'Unknown Student',
        studentEmail: currentUser.email,
        programName: newLorRequest.programName.trim(),
        deadline: newLorRequest.deadline,
        purpose: newLorRequest.purpose.trim(),
        emphasisPoints: newLorRequest.emphasisPoints.trim(),
        additionalInfo: newLorRequest.additionalInfo.trim(),
        status: 'pending',
        timestamp: new Date().toISOString(),
        completedAt: null
      });
      
      setNewLorRequest({
        programName: '',
        deadline: '',
        purpose: '',
        emphasisPoints: '',
        additionalInfo: ''
      });
      
      alert('Letter of recommendation request submitted successfully!');
    } catch (error) {
      console.error('Error submitting LoR request:', error);
      alert('Failed to submit request. Please try again.');
    }
  };

  // Mark a request as complete (teacher)
  const handleCompleteLorRequest = async (requestId) => {
    try {
      await updateDoc(doc(db, 'lorRequests', requestId), {
        status: 'completed',
        completedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error completing LoR request:', error);
      alert('Failed to mark request as complete. Please try again.');
    }
  };

  // Delete a request
  const handleDeleteLorRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this letter of recommendation request?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'lorRequests', requestId));
    } catch (error) {
      console.error('Error deleting LoR request:', error);
      alert('Failed to delete request. Please try again.');
    }
  };

  return {
    lorRequests,
    newLorRequest,
    setNewLorRequest,
    handleSubmitLorRequest,
    handleCompleteLorRequest,
    handleDeleteLorRequest
  };
};

/**
 * Letter of Recommendation Request Form Component (Student)
 */
export const LorRequestForm = ({ newLorRequest, setNewLorRequest, handleSubmitLorRequest }) => {
  return (
    <div className="create-post">
      <h2>Request a Letter of Recommendation</h2>
      <p className="transcript-description">
        Submit a request for a letter of recommendation. Provide details about what you're applying for and what you'd like emphasized.
      </p>
      
      <div className="form-group">
        <label>Program/Position Name: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <input
          type="text"
          placeholder="e.g., MIT Computer Science Program"
          value={newLorRequest.programName}
          onChange={(e) => setNewLorRequest({ ...newLorRequest, programName: e.target.value })}
          className="post-title-input"
        />
      </div>

      <div className="form-group">
        <label>Deadline: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <input
          type="date"
          value={newLorRequest.deadline}
          onChange={(e) => setNewLorRequest({ ...newLorRequest, deadline: e.target.value })}
          className="post-title-input"
        />
      </div>

      <div className="form-group">
        <label>Purpose: <span style={{color: 'var(--color-error)'}}>*</span></label>
        <textarea
          placeholder="What are you applying for? (e.g., college admission, scholarship, internship, etc.)"
          value={newLorRequest.purpose}
          onChange={(e) => setNewLorRequest({ ...newLorRequest, purpose: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <div className="form-group">
        <label>What would you like emphasized? (Optional)</label>
        <textarea
          placeholder="Specific skills, achievements, or qualities you'd like highlighted in the letter..."
          value={newLorRequest.emphasisPoints}
          onChange={(e) => setNewLorRequest({ ...newLorRequest, emphasisPoints: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <div className="form-group">
        <label>Additional Information (Optional)</label>
        <textarea
          placeholder="Any other details that would help write the letter (e.g., program requirements, specific formats needed, etc.)"
          value={newLorRequest.additionalInfo}
          onChange={(e) => setNewLorRequest({ ...newLorRequest, additionalInfo: e.target.value })}
          className="post-description-input"
          rows="4"
        />
      </div>

      <button 
        onClick={handleSubmitLorRequest} 
        className="class-assignment-button"
        disabled={!newLorRequest.programName.trim() || !newLorRequest.deadline || !newLorRequest.purpose.trim()}
      >
        Submit Request
      </button>
    </div>
  );
};

/**
 * Student's Own LoR Requests Display Component
 */
export const LorRequestsDisplay = ({ lorRequests, currentUser }) => {
  const myRequests = lorRequests.filter(req => req.studentId === currentUser.uid);
  
  if (myRequests.length === 0) {
    return null;
  }

  // Helper function to calculate urgency
  const getRequestUrgency = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    return {
      isOverdue: daysUntilDeadline < 0,
      isUrgent: daysUntilDeadline >= 0 && daysUntilDeadline < 3,
      daysUntilDeadline
    };
  };

  return (
    <div className="post">
      <h3>Your Letter of Recommendation Requests</h3>
      <div className="mentor-requests-list">
        {myRequests.map((request) => {
          const urgency = getRequestUrgency(request.deadline);
          
          return (
            <div key={request.id} className="mentor-request-item">
              <div className="mentor-request-header">
                <strong className="mentor-request-status">
                  {request.programName}
                  {urgency.isOverdue && request.status === 'pending' && (
                    <span className="lor-request-overdue-badge">
                      <i className="fa-solid fa-exclamation-triangle"></i> OVERDUE
                    </span>
                  )}
                  {request.status === 'completed' && (
                    <span className="mentor-request-completed-badge">
                      <i className="fa-solid fa-check"></i> Completed
                    </span>
                  )}
                </strong>
                <span className="timestamp">
                  Deadline: {new Date(request.deadline).toLocaleDateString()}
                </span>
              </div>
              <div className="mentor-request-description">
                <strong>Purpose:</strong> {request.purpose}
              </div>
              {request.emphasisPoints && (
                <div className="mentor-request-description">
                  <strong>Emphasis Points:</strong> {request.emphasisPoints}
                </div>
              )}
              <div className="mentor-request-email">
                Requested on {new Date(request.timestamp).toLocaleDateString()}
              </div>
              {request.status === 'completed' && request.completedAt && (
                <div className="lor-request-complete-info">
                  <i className="fa-solid fa-check lor-request-check-icon"></i>
                  Request completed {new Date(request.completedAt).toLocaleDateString()}
                </div>
              )}
              {request.status === 'pending' && (
                <div className="mentor-request-email" style={{marginTop: 'var(--spacing-sm)', fontStyle: 'italic'}}>
                  Status: Pending review
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Teacher LoR Management View Component
 */
export const LorManagementView = ({ lorRequests, handleCompleteLorRequest, handleDeleteLorRequest }) => {
  // Helper function to calculate urgency
  const getRequestUrgency = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysUntilDeadline = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    return {
      isOverdue: daysUntilDeadline < 0,
      isUrgent: daysUntilDeadline >= 0 && daysUntilDeadline < 3,
      daysUntilDeadline
    };
  };

  return (
    <div className="post post-margin-bottom">
      <h2>Letter of Recommendation Requests</h2>
      <p className="transcript-description">
        Review and manage letter of recommendation requests from students.
      </p>

      {lorRequests.length === 0 ? (
        <p className="no-posts">No letter of recommendation requests yet.</p>
      ) : (
        <>
          {/* Pending Requests */}
          {(() => {
            const pendingRequests = lorRequests.filter(req => req.status === 'pending');
            if (pendingRequests.length === 0) return null;
            
            // Sort by deadline: overdue first, then urgent (< 3 days), then by date
            const sortedRequests = [...pendingRequests].sort((a, b) => {
              const dateA = new Date(a.deadline);
              const dateB = new Date(b.deadline);
              return dateA - dateB;
            });
            
            return (
              <>
                <h3 style={{ color: 'var(--color-primary)', marginTop: '20px', marginBottom: '15px' }}>Pending Requests</h3>
                {sortedRequests.map((request) => {
                  const urgency = getRequestUrgency(request.deadline);
                  const itemClassName = `lor-request-item${urgency.isUrgent ? ' lor-request-urgent' : ''}${urgency.isOverdue ? ' lor-request-overdue' : ''}`;
                  
                  return (
                    <div key={request.id} id={`lor-request-${request.id}`} className={itemClassName}>
                      <div className="lor-request-header">
                        <div>
                          <h4 className="lor-request-student-name">
                            {request.studentName}
                            {urgency.isOverdue && (
                              <span className="lor-request-overdue-badge">
                                <i className="fa-solid fa-exclamation-triangle"></i> OVERDUE
                              </span>
                            )}
                          </h4>
                          <p className="lor-request-email">{request.studentEmail}</p>
                        </div>
                        <div className={`lor-request-deadline${urgency.isOverdue ? ' lor-deadline-overdue' : urgency.isUrgent ? ' lor-deadline-urgent' : ''}`}>
                          <strong>Deadline:</strong> {new Date(request.deadline).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="lor-request-body">
                        <div className="lor-request-field">
                          <strong>Program/Position:</strong>
                          <p>{request.programName}</p>
                        </div>

                        <div className="lor-request-field">
                          <strong>Purpose:</strong>
                          <p>{request.purpose}</p>
                        </div>

                        {request.emphasisPoints && (
                          <div className="lor-request-field">
                            <strong>Emphasis Points:</strong>
                            <p>{request.emphasisPoints}</p>
                          </div>
                        )}

                        {request.additionalInfo && (
                          <div className="lor-request-field">
                            <strong>Additional Information:</strong>
                            <p>{request.additionalInfo}</p>
                          </div>
                        )}

                        <div className="lor-request-timestamp">
                          Requested on {new Date(request.timestamp).toLocaleDateString()}
                        </div>
                      </div>

                      <div className="lor-request-actions">
                        <button 
                          onClick={() => handleCompleteLorRequest(request.id)} 
                          className="save-btn"
                        >
                          Mark as Complete
                        </button>
                        <button 
                          onClick={() => handleDeleteLorRequest(request.id)} 
                          className="delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {/* Completed Requests */}
          {(() => {
            const completedRequests = lorRequests.filter(req => req.status === 'completed');
            if (completedRequests.length === 0) return null;
            
            // Sort by completion date (most recent first)
            const sortedCompletedRequests = [...completedRequests].sort((a, b) => {
              const dateA = new Date(a.completedAt || 0);
              const dateB = new Date(b.completedAt || 0);
              return dateB - dateA;
            });
            
            return (
              <>
                <h3 style={{ color: 'var(--color-text-tertiary)', marginTop: '40px', marginBottom: '15px' }}>Completed Requests</h3>
                {sortedCompletedRequests.map((request) => (
                  <div key={request.id} id={`lor-request-${request.id}`} className="lor-request-item lor-request-completed">
                    <div className="lor-request-header">
                      <div>
                        <h4 className="lor-request-student-name">{request.studentName}</h4>
                        <p className="lor-request-email">{request.studentEmail}</p>
                      </div>
                      <div className="lor-request-completed-badge">
                        <i className="fa-solid fa-check"></i> Completed
                      </div>
                    </div>

                    <div className="lor-request-body">
                      <div className="lor-request-field">
                        <strong>Program/Position:</strong>
                        <p>{request.programName}</p>
                      </div>

                      <div className="lor-request-timestamp">
                        Completed on {new Date(request.completedAt).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="lor-request-actions">
                      <button 
                        onClick={() => handleDeleteLorRequest(request.id)} 
                        className="delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
};

const lorComponents = {
  useLorManagement,
  LorRequestForm,
  LorRequestsDisplay,
  LorManagementView
};

export default lorComponents;

